import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { ShieldCheck, ChevronLeft } from "lucide-react";

export default function CommunityGuidelines() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="community-guidelines-page">
      <Link to="/student/friends" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" /> Back
      </Link>

      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 inline-flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" /> PrepMasters
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Community Guidelines</h1>
        <p className="text-slate-600 mt-2">
          PrepMasters is a study community. These rules keep it safe and useful for every student
          preparing for their board exam or civil service exam. Last updated: 27 May 2026.
        </p>
      </div>

      <Card className="p-6 border-slate-200">
        <h2 className="font-display font-bold text-xl">1. Be kind. Be respectful.</h2>
        <p className="text-sm text-slate-700 mt-2">
          PrepMasters is for celebrating each other's progress. We don't tolerate harassment,
          bullying, slurs, hate speech, or personal attacks. Period.
        </p>

        <h2 className="font-display font-bold text-xl mt-6">2. No impersonation.</h2>
        <p className="text-sm text-slate-700 mt-2">
          Use a display handle that represents you honestly. You may not impersonate another
          person, a brand, a public figure, or an official PrepMasters account.
        </p>

        <h2 className="font-display font-bold text-xl mt-6">3. No adult, violent, or illegal content.</h2>
        <p className="text-sm text-slate-700 mt-2">
          No sexual content, no graphic violence, no content that endangers minors, no content
          promoting illegal activity. PrepMasters has under-18 users; we hold a strict line.
        </p>

        <h2 className="font-display font-bold text-xl mt-6">4. No spam.</h2>
        <p className="text-sm text-slate-700 mt-2">
          Don't send mass friend requests, don't post off-topic links, don't promote unrelated
          services. Friend requests are rate-limited automatically.
        </p>

        <h2 className="font-display font-bold text-xl mt-6">5. Respect privacy.</h2>
        <p className="text-sm text-slate-700 mt-2">
          Don't share other students' full names, contact details, or screenshots without their
          consent. Don't post identifying info about real exam questions either — protect
          ongoing testing integrity.
        </p>

        <h2 className="font-display font-bold text-xl mt-6">6. We're stricter with minors.</h2>
        <p className="text-sm text-slate-700 mt-2">
          Students under 18 default to fully-private settings. We never display their full
          name in social features. If you're an adult interacting with a minor friend,
          you're expected to keep things appropriate and exam-focused.
        </p>
      </Card>

      <Card className="p-6 border-slate-200">
        <h2 className="font-display font-bold text-xl">How reports work</h2>
        <ul className="text-sm text-slate-700 mt-3 space-y-2 list-disc ml-5">
          <li>You can <strong>report</strong> any other student from their profile or any friend card.</li>
          <li>Severe categories (harassment, sexual content involving a minor, self-harm, violence) trigger an <strong>immediate auto-hide</strong> of the reported account pending human review.</li>
          <li>Three or more reports from different students against the same account also trigger an auto-hide.</li>
          <li>Our moderation team reviews every report within <strong>24 hours</strong>.</li>
          <li>You can also <strong>block</strong> any student. Blocking is silent — they aren't told. It also unfriends you automatically.</li>
        </ul>
      </Card>

      <Card className="p-6 border-slate-200">
        <h2 className="font-display font-bold text-xl">Enforcement</h2>
        <p className="text-sm text-slate-700 mt-2">
          Violations may result in: a warning, a temporary suspension, permanent account
          deletion, and/or referral to authorities for unlawful conduct. We retain audit logs
          of friend requests, reports, and moderation actions for incident response under the
          Philippine Data Privacy Act (RA 10173).
        </p>
      </Card>

      <Card className="p-6 border-slate-200">
        <h2 className="font-display font-bold text-xl">Reach our moderators</h2>
        <p className="text-sm text-slate-700 mt-2">
          See something we missed? Tap the flag icon on any profile to report, or contact
          support from your account page. Severe safety issues (e.g., a minor in danger) —
          please also contact local authorities; PrepMasters cooperates fully with law
          enforcement requests.
        </p>
      </Card>

      <p className="text-center text-[11px] text-slate-500">
        Disagree with a moderation decision? You can appeal once via your account's Support page.
      </p>
    </div>
  );
}
